import { cartLineId } from "../utils/variantCore";
import React, { useState, useEffect, useMemo, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Layout from "../components/Global/Layout";
import OpenInAppBanner from "../components/OpenInAppBanner";
import { extractVendorId } from "../utils/vendorSlug";
import { SEOHead, stripHtmlForSeo } from "../components/SEO/SEOHead";
import { shopPath as storefrontPathFor, productPathUnder } from "../utils/seoPaths";
import { addToCart, moveCartToOutlet, removeFromCart } from "../redux/brandCartSlice";
import { addToCart as trackAddToCart } from "../utils/analytics";
import {
  cartOutletId,
  findOutlet,
  outletIdFromSearch,
  vendorLocationsOf,
  withOutletQuery,
} from "../utils/vendorOutlets";
import OutletSwitchDialog from "../components/OutletSwitchDialog";
import { hereNameOf, locationName } from "../components/LocationPicker";
/* Branch operations (4 Oct 2026): the product as it stands at this branch,
   "Get it from" another, and the cart check after a move. */
import {
  MAIN,
  addButtonLabel,
  applyBranchEventToProduct,
  blockedHere,
  soldOutLabel,
} from "../utils/branchAvailability";
import { branchProductChanged } from "../utils/branchCart";
import useCatalogUpdates from "../hooks/useCatalogUpdates";
import BranchAvailabilityBadge from "../components/branch/BranchAvailabilityBadge";
import BranchCartReviewDialog from "../components/branch/BranchCartReviewDialog";
import { useCartBranchCheck } from "../hooks/useBranchCartCheck";
import useDeliveryPoint from "../hooks/useDeliveryPoint";
import { calculateDistance } from "../redux/userLocationSlice";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import placeholderImage from "../assets/img/menuImage.png";
import DOMPurify from "dompurify";
import {
  Badge,
  IconButton,
  Modal,
  Backdrop,
  Fade,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShareIcon from "@mui/icons-material/Share";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import BrandCart from "../components/BrandCart";
import BrandProductOptions, { productOptionsDelta, missingProductChoice } from "../components/BrandProductOptions";
/* THE one product media viewer, shared with Product.jsx, the retailer and
   wholesaler pages, the restaurant dialog and the healthcare dialog. The
   local ZoomGallery this page used to define lived here and was copied into
   two other files; it is now that component's hover-zoom half. */
import ProductImageGallery from "../components/ProductImageGallery";
import OfficialStoreBadge from "../components/OfficialStoreBadge";
import { getBrandTheme, getBrandThemeCssVars } from "../utils/brandTheme";
import Reviews from "../components/Reviews/Reviews";
import { CURRENCY, CURRENCY_LOCALE, formatCount } from "../config/market";
import DealTag from "../components/DealTag";
import PriceCut from "../components/PriceCut";
import { hasSalePrice, listPriceOf, unitSavingsOf } from "../utils/vendorDiscounts";
/* ONE rule for "is this sold by quote, and what does its price row say", shared
   with BrandShopPage, the healthcare storefront, search, the share text and both
   customer apps. This page used to read `product.priceHidden` on its own, which
   only carries the posture on a payload that has been through the server's
   mapProductForPublic: GET /api/brands/:id/products/:productId returned raw
   documents until today, which is how a cold load of this page came to render
   "USh 0" beside a live Add to cart on an item the server refuses to sell. */
import {
  PRICE_ON_REQUEST_LABEL,
  PRICE_UNAVAILABLE_LABEL,
  canRequestQuote,
  describeQuoteProgress,
  isQuoteOnly,
  publishedPrice,
} from "../utils/priceOnRequest";
/* The buyer's own quote requests, read the same way BrandShopPage reads them so
   the product page and the storefront it sits under cannot disagree about where
   one request has got to. */
import useBuyerQuoteRequests, {
  duplicateQuoteRequestNotice,
} from "../hooks/useBuyerQuoteRequests";

const fmtUgx = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat(CURRENCY_LOCALE, {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 0,
      }).format(Number(n));

/**
 * WHAT THE PRIMARY BUTTON SHOULD DO.
 *
 * Extracted and exported so the rule can be tested without mounting a page
 * that needs a store, a router and a network.
 *
 * Once the product is in the cart the stepper edits the cart line directly
 * (see brandQtyStep), so this button has nothing left to apply and becomes
 * the way to the cart. It used to have UPDATE and REMOVE states as well, fed
 * by a local number that was allowed to differ from the cart. That is how the
 * page came to show a quantity of 0 beside "1 in cart" while both badges
 * still counted 1: stepping down changed nothing until a second button was
 * pressed.
 *
 * THREE STATES OF PRICE, not two. `priceHidden` is a quote-only posture: there
 * is a price, behind a conversation, so the button asks for it. `unpriced` is a
 * SKU nobody has costed yet, which has no posture behind it and no quote to ask
 * for, so there is nothing for this button to do at all. The two used to share
 * one branch, which is why an unpriced import offered a Get Quote it had no
 * vendor posture to back, and why a quote-only product that still carried a
 * price from before the vendor flipped the posture offered Add to cart.
 *
 * THREE QUOTE STATES, not one. A quote is a conversation, and a shopper who
 * has already started one must not be offered the same "Request a quote" as
 * someone who never has (founder rule, 29 Sep 2026: the progress of the
 * interaction stays visible until the sale concludes). While a request is live
 * the only useful move is to go and look at it, so the button carries the
 * progress action and leads to My Quotes; once the conversation has finished,
 * however it finished, asking again is allowed and the button says so.
 *
 * Deliberately keyed on `quoteProgress.live` rather than on a list of
 * lifecycles repeated here: that is the same test `canRequestQuote` applies, so
 * the button and the modal guard cannot come to different conclusions about one
 * request.
 *
 * @param {{priceHidden?: boolean, unpriced?: boolean, inCartQty?: number,
 *          quoteProgress?: {live?: boolean, action?: string}|null}} args
 *        `quoteProgress` is describeQuoteProgress()'s answer for this buyer's
 *        own request, or null when they have never asked.
 * @returns {"QUOTE"|"QUOTE_IN_PROGRESS"|"QUOTE_AGAIN"|"UNAVAILABLE"|"ADD"|"GO_TO_CART"}
 */
export const brandCtaAction = ({
  priceHidden = false,
  unpriced = false,
  inCartQty = 0,
  quoteProgress = null,
} = {}) => {
  if (priceHidden) {
    if (!quoteProgress) return "QUOTE";
    return quoteProgress.live ? "QUOTE_IN_PROGRESS" : "QUOTE_AGAIN";
  }
  if (unpriced) return "UNAVAILABLE";
  return inCartQty > 0 ? "GO_TO_CART" : "ADD";
};

/**
 * WHAT A PRESS OF PLUS OR MINUS DOES.
 *
 * Out of the cart the stepper picks how many to add (PICK), bounded by the
 * minimum order and the stock. In the cart it IS the cart line: every press
 * lands in the store at once (CART, a signed delta), so the number shown, the
 * button label and both badges always agree. Stepping below the minimum takes
 * the line out (REMOVE), the same rule the cart page applies at 1. BLOCKED is
 * a press with nothing to do: minus shakes, plus is disabled.
 *
 * @returns {{type: "PICK", quantity: number} | {type: "CART", delta: number} | {type: "REMOVE"} | {type: "BLOCKED"}}
 */
export const brandQtyStep = ({
  direction = 1,
  inCartQty = 0,
  quantity = 0,
  minQty = 1,
  maxQty = 0,
} = {}) => {
  const min = Math.max(1, Number(minQty) || 1);
  const max = Math.max(0, Number(maxQty) || 0);
  const inCart = Number(inCartQty) || 0;
  const picked = Number(quantity) || 0;
  if (inCart > 0) {
    if (direction < 0) {
      return inCart - 1 < min ? { type: "REMOVE" } : { type: "CART", delta: -1 };
    }
    return inCart < max ? { type: "CART", delta: 1 } : { type: "BLOCKED" };
  }
  if (direction < 0) {
    return picked > min
      ? { type: "PICK", quantity: picked - 1 }
      : { type: "BLOCKED" };
  }
  return picked < max
    ? { type: "PICK", quantity: picked + 1 }
    : { type: "BLOCKED" };
};

const BrandProductPage = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  /* ── Cold load ────────────────────────────────────────────────────────
   * This page used to read `location.state` and nothing else, so it rendered
   * "Product not found." for anyone who did not arrive by clicking through
   * from the brand shop — which is everyone who scans a QR code, follows a
   * shared link, refreshes, or opens the URL in a new tab.
   *
   * State still wins when present (instant render, no spinner for in-app
   * navigation); the params are the fallback that makes the URL shareable. */
  const params = useParams();
  const state = location.state || {};

  /* Both segments may carry a readable slug ("dudu-krin-pet-shampoo--<id>"),
     the address search engines are given (src/utils/seoPaths.js). Only the
     id after the last "--" is looked up; a bare id passes through unchanged. */
  const routeProductId = extractVendorId(params.productId) || state.product?._id || null;
  // The outlet (branch) the shopper is buying from, carried over from the
  // storefront in `?outletId=` (route state is the fallback for an in-app
  // hop). It is stamped on every cart line added here and kept on the links
  // back to the storefront, so the whole visit stays on one outlet.
  const outletId = outletIdFromSearch(location.search) || state.outletId || null;

  const [fetched, setFetched] = useState({ product: null, brand: null });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // Legacy URL support: /brand/product/:productId carries no brand id (it was
  // the in-app route, and it is what the address bar showed until Aug 2026,
  // so it is in shared messages and QR codes). Resolve the brand from the
  // product itself, then let the normal cold-load path run.
  const [resolvedBrandId, setResolvedBrandId] = useState(null);

  const productBrandRef = state.product?.brandId;
  const productBrandId =
    productBrandRef && typeof productBrandRef === "object" ? productBrandRef._id : productBrandRef;
  const routeBrandId =
    extractVendorId(params.brandId) || state.brandId || productBrandId || resolvedBrandId || null;

  /* Route state paints instantly, then the authoritative endpoint wins. The
     sections payload historically omitted videos, so letting route state win
     forever made in-store navigation permanently less complete than a cold
     load of the exact same URL. */
  const product = fetched.product || state.product;
  const brand = fetched.brand || state.brand;
  const brandId = routeBrandId;

  useEffect(() => {
    if (routeBrandId || !routeProductId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const base = process.env.REACT_APP_SERVER_URL || "";
        const res = await fetch(`${base}/api/brands/products/${routeProductId}`);
        const json = await res.json();
        const found = json?.data?.brandId;
        const id = found && typeof found === "object" ? found._id : found;
        if (cancelled) return;
        if (id) {
          // The normal cold-load effect takes over from here (and owns the
          // spinner) now that the brand id is known.
          setResolvedBrandId(String(id));
        } else {
          setLoadError("This product is no longer available.");
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError("We couldn't load this product. Please try again.");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeBrandId, routeProductId]);

  useEffect(() => {
    if (!routeBrandId || !routeProductId) return undefined;

    let cancelled = false;
    setLoading(true);
    setLoadError("");
    (async () => {
      try {
        const base = process.env.REACT_APP_SERVER_URL || "";
        /* Both reads name the branch (4 Oct 2026), so the product comes back
           as it stands there: sold out or switched off there, the price
           there. "main" is the brand's own store. */
        const at = outletId || MAIN;
        const [productRes, brandRes] = await Promise.all([
          fetch(
            withOutletQuery(
              `${base}/api/brands/${routeBrandId}/products/${routeProductId}`,
              at,
            ),
          ),
          fetch(withOutletQuery(`${base}/api/brands/${routeBrandId}`, at)),
        ]);
        const productJson = await productRes.json();
        const brandJson = await brandRes.json();
        if (cancelled) return;
        const nextProduct = productJson?.data || null;
        const nextBrand = brandJson?.data || brandJson || null;
        if (!nextProduct) {
          setLoadError("This product is no longer available.");
        }
        setFetched({ product: nextProduct, brand: nextBrand });
      } catch (err) {
        if (!cancelled)
          setLoadError("We couldn't load this product. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeBrandId, routeProductId, outletId, state.product, state.brand]);

  const { carts } = useSelector((s) => s.brandCart);
  const currentUser = useSelector((s) => s.user?.currentUser);
  const authToken =
    currentUser?.token ||
    currentUser?.accessToken ||
    currentUser?.result?.token;
  const cart = carts[brandId] || {};
  const cartCount = Object.values(cart).reduce(
    (t, i) => t + (Number(i.quantity) || 0),
    0,
  );
  // The outlet the cart's existing lines were added from (null for the main
  // store). An add from a different outlet asks before clearing them.
  const cartOutlet = cartOutletId(cart);
  const [showCart, setShowCart] = useState(false);
  // The add that is waiting on the shopper's answer to "Switch outlet?":
  // the cart delta to apply once the cart has been cleared.
  const [pendingOutletAdd, setPendingOutletAdd] = useState(null);
  const shopPath = withOutletQuery(`/brand/shop/${brandId}`, outletId);

  const brandTheme = getBrandTheme(brand);
  const { primary, secondary, onPrimary, onSecondary } = brandTheme;
  const tplFields = brand?.productSpecTemplate?.fields || [];

  const opts = Array.isArray(product?.specOptions) ? product.specOptions : [];
  const [selectedSpecs, setSelectedSpecs] = useState({});
  const selectedLineId = cartLineId({ _id: product?._id, selectedSpecs });
  const existingCartItem = cart[selectedLineId];
  /* The PRIMITIVE, deliberately. Depending on `existingCartItem` itself would
     re-run the sync below on every render, because the selector hands back a
     fresh object each time, and that would reset the selector under a buyer's
     fingers the moment they pressed + or -. Depending on the number means the
     sync fires when the CART changes and at no other time. Extracted rather
     than written inline so react-hooks/exhaustive-deps is satisfied honestly:
     an eslint-disable here has broken a build in this repo before. */
  const cartQuantity = existingCartItem
    ? Number(existingCartItem.quantity) || 0
    : 0;
  const minQty = product?.minOrderQty || 1;

  /* HOW MANY TO ADD, for a product that is NOT in the cart yet.
     Once the product is in the cart the stepper shows and edits the cart line
     itself (`shownQty` below), so this number never stands in for the cart and
     cannot drift from it. It returns to the minimum whenever the line changes,
     so a product taken out of the cart offers the minimum again rather than a
     stale pick. Depending on `cartQuantity`, a primitive, keeps a buyer's pick
     from being reset on every render. */
  const [quantity, setQuantity] = useState(minQty);
  useEffect(() => {
    setQuantity(minQty);
  }, [cartQuantity, minQty]);

  const [atMin, setAtMin] = useState(false);


  /* WHERE THE BUYER'S OWN REQUEST HAS GOT TO.
     A quote is a conversation, and until now this page showed none of it: a
     buyer who had asked and was waiting came back to the same "Request a quote"
     button as someone who never had, with the only account of their request on
     a different screen entirely. Read only for a quote-only product and only
     for a signed-in shopper (the endpoint is behind `auth`), and a failed read
     simply reads as "no request", so nothing on this page waits on it. Sits
     above the early returns below because it is a hook. */
  const quoteProductId = product?._id || routeProductId;
  const { requestFor: buyerQuoteRequestFor } = useBuyerQuoteRequests({
    productId: quoteProductId,
    enabled: isQuoteOnly(product),
  });
  const buyerQuoteRequest = buyerQuoteRequestFor(quoteProductId);

  /* Every place this brand sells from, for "Get it from" when the product
     cannot be had at this branch, and the cart check: quiet while browsing,
     the review dialog after the cart moves to another branch. Hooks, so above
     the early returns. */
  const deliveryPoint = useDeliveryPoint();
  const brandLocations = useMemo(
    () => (brand ? vendorLocationsOf(brand, { deliveryPoint, calculateDistance }) : []),
    [brand, deliveryPoint],
  );
  const branchCheck = useCartBranchCheck({
    vertical: "brand",
    cartId: brandId,
    checkWhen: Boolean(brandId),
    silent: true,
  });
  // "Get it from <branch>" waiting on "Move your cart to <branch>?", or null.
  const [pendingGetItFrom, setPendingGetItFrom] = useState(null);
  /* A branch switched this product on or off, or sold it out, while the page
     is open: the badge and the button follow in place, and a cart line it
     touches is checked again before the shopper pays. */
  useCatalogUpdates(
    {
      "branchProduct:changed": (payload) => {
        dispatch(branchProductChanged(payload));
        setFetched((previous) =>
          previous.product
            ? { ...previous, product: applyBranchEventToProduct(previous.product, payload) }
            : previous,
        );
      },
    },
    { enabled: Boolean(routeProductId) },
  );

  const detailColRef = useRef(null);
  const [activeInfoTab, setActiveInfoTab] = useState("description");
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyLink = () => {
    try {
      const url = window.location.href;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url);
      }
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
      toast.success("Product link copied");
    } catch (err) {
      toast.error("Couldn't copy link");
    }
  };

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const shareData = {
      title: product?.name,
      text: `${product?.name} — on ${brand?.tradingName || brand?.name}`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (_) {
        /* user cancelled */
      }
    } else {
      handleCopyLink();
    }
  };

  const appBanner = (
    <OpenInAppBanner label="Open this product in the Lusuku app" />
  );

  if (loading && !product) {
    return (
      <Layout>
        <Wrap>
          <p style={{ padding: "4rem", textAlign: "center", color: "#64748b" }}>
            Loading product…
          </p>
        </Wrap>
      </Layout>
    );
  }

  if (!product || !brand) {
    return (
      <Layout>
        <Wrap>
          <p style={{ padding: "4rem", textAlign: "center", color: "#64748b" }}>
            {loadError || "Product not found."}{" "}
            <button
              onClick={() => history.goBack()}
              style={{
                color: primary,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Go back
            </button>
          </p>
        </Wrap>
      </Layout>
    );
  }

  /* At this branch (4 Oct 2026). The main store goes by the brand's name. A
     quote-only product keeps its own posture: a quote is always available. */
  const brandName = brand.tradingName || brand.name || "";
  // Named as the apps name it: "<brand> (main branch)" for the main store of
  // a brand with branches, the brand's own name for a one-shop brand.
  const hereName = hereNameOf(brandLocations, outletId ? findOutlet(brand, outletId) : null, brandName);
  const branchBlocked = !isQuoteOnly(product) && blockedHere(product);
  /* "Get it from <branch>": open this product at that branch. The cart moves
     along (it is fulfilled from one branch) and is checked there. With items
     from another branch in the cart it asks first, as the location picker
     and the apps do (shopper journey audit, 4 Oct 2026). */
  const getItFrom = (picked) => {
    const target = picked?.isOutlet ? String(picked._id) : null;
    if (cartCount > 0 && (cartOutlet || null) !== target) {
      setPendingGetItFrom(picked);
      return;
    }
    openWithCart(picked);
  };
  const openWithCart = (picked) => {
    const target = picked?.isOutlet ? String(picked._id) : null;
    if (cartCount > 0) {
      dispatch(moveCartToOutlet({ brandId, outletId: target }));
      branchCheck.reviewNextCheck();
    }
    history.replace({
      pathname: location.pathname,
      search: withOutletQuery("", target),
      state: { ...state, outletId: target },
    });
  };

  const labeledOptions = opts.map((option) => ({ ...option,
    label: option.label || tplFields.find((field) => field.fieldKey === option.fieldKey)?.label || option.fieldKey,
  }));
  const computeDelta = (picks) => productOptionsDelta(opts, picks);
  const findMissing = (picks) => missingProductChoice(labeledOptions, picks);

  /* ONE WAY ONTO THE CART LINE. The Add button and the in-cart plus and minus
     all come through here, so the line price, the spec picks and the
     analytics event are built in one place. Returns false when nothing was
     applied (including when the add is parked behind the outlet prompt). */
  const applyCartDelta = (delta, { afterOutletSwitch = false } = {}) => {
    if (!delta) return false;
    const specPriceDelta = computeDelta(selectedSpecs);
    if (delta > 0) {
      const missing = findMissing(selectedSpecs);
      if (missing) {
        toast.warning(
          `Please choose a value for "${missing}" before adding to cart.`,
        );
        return false;
      }
      /* The cart is fulfilled from ONE outlet (every line carries the same
         outletId, the order names it as fulfilmentOutletId). Adding from a
         different outlet than the lines already in the cart asks first, and
         on a yes the lines MOVE to this outlet. A branch can have its own
         sold out, on/off and price (4 Oct 2026), so the moved lines are
         checked there and anything that changed is shown before the shopper
         pays. The storefront's location picker does the same. */
      if (
        !afterOutletSwitch &&
        cartCount > 0 &&
        (cartOutlet || null) !== (outletId || null)
      ) {
        // The cart is kept (it moves), so only this add is still to apply.
        setPendingOutletAdd({ delta });
        return false;
      }
      trackAddToCart({
        id: product._id,
        name: product.name,
        // The unit price the cart line (and the server) will use.
        price: hasSalePrice(product)
          ? product.salePrice
          : (product.displayPrice ?? product.price),
        quantity: delta,
        vendorName: brand?.name,
      });
    }
    dispatch(
      addToCart({
        brandId,
        product: {
          ...product,
          /* `publishedPrice` is `displayPrice ?? price` with one extra rule: it
             refuses anything that is not a finite number above zero. A line
             stored at 0 reads as priced everywhere downstream and then dies at
             order-create with PRICE_UNSET, so storing null instead keeps the
             cart's own account of the line honest. */
          price: publishedPrice(product),
          selectedSpecs: { ...selectedSpecs },
          specPriceDelta,
          // The outlet this line is fulfilled from; absent for the main store.
          ...(outletId ? { outletId } : {}),
        },
        quantity: delta,
        selectedSpecs,
        specPriceDelta,
      }),
    );
    return true;
  };

  const confirmOutletAdd = () => {
    if (!pendingOutletAdd) return;
    dispatch(moveCartToOutlet({ brandId, outletId: outletId || null }));
    branchCheck.reviewNextCheck();
    const { delta } = pendingOutletAdd;
    setPendingOutletAdd(null);
    if (applyCartDelta(delta, { afterOutletSwitch: true })) {
      toast.success(`${product.name} added to cart`);
    }
  };

  const handleAddToCart = () => {
    if (isQuoteOnly(product)) {
      toast.info("Please request a quote for this product.");
      return;
    }
    // Sold out or switched off at this branch: "Get it from" offers another.
    if (blockedHere(product)) {
      toast.info(soldOutLabel(product, hereName));
      return;
    }
    /* Not the same thing as the above, and it needs different words: there is
       no quote to ask for on a SKU that has simply never been costed. */
    if (publishedPrice(product) === null) {
      toast.info(
        "This item has no price yet, so it cannot be ordered. Ask the brand to publish a price.",
      );
      return;
    }
    const desiredTotal = Math.min(quantity, product.countInStock || 0);
    if (desiredTotal <= 0) {
      toast.error("Not enough stock available!");
      return;
    }
    if (desiredTotal < minQty) {
      toast.warning(
        `Minimum order quantity is ${formatCount(minQty)} unit${minQty !== 1 ? "s" : ""}.`,
      );
      return;
    }
    if (applyCartDelta(desiredTotal - cartQuantity)) {
      toast.success(`${product.name} added to cart`);
    }
  };

  const handleRemoveFromCart = () => {
    dispatch(removeFromCart({ brandId, productId: selectedLineId }));
    toast.success(`${product.name} removed from cart`);
  };

  /* A product switched off can still be taken out of the cart, never added. */
  const stepArgs = {
    inCartQty: cartQuantity,
    quantity,
    minQty,
    maxQty: product.enabled ? product.countInStock || 0 : 0,
  };
  const stepDown = brandQtyStep({ ...stepArgs, direction: -1 });
  const stepUp = brandQtyStep({ ...stepArgs, direction: 1 });
  const shownQty = cartQuantity > 0 ? cartQuantity : quantity;
  const removesLine = stepDown.type === "REMOVE";

  const handleStep = (step) => {
    if (step.type === "PICK") setQuantity(step.quantity);
    else if (step.type === "CART") applyCartDelta(step.delta);
    else if (step.type === "REMOVE") handleRemoveFromCart();
    else {
      setAtMin(true);
      setTimeout(() => setAtMin(false), 600);
    }
  };

  const handleRequestQuote = () => {
    /* ONE live request per product. Two open requests give the vendor two
       things to answer and the buyer two places to look, so a shopper who has
       already asked is sent to the conversation they already have rather than
       silently starting a second one. `canRequestQuote` is the same test the
       button state above is keyed on and the same one BrandShopPage applies
       before opening the modal. */
    if (!canRequestQuote(buyerQuoteRequest)) {
      toast.info(
        duplicateQuoteRequestNotice({
          productName: product.name,
          vendorName: brand?.tradingName || brand?.name,
        }),
        { autoClose: 8000 },
      );
      return;
    }

    // Persist the intent so it survives a /login round-trip (location.state is
    // lost across the post-login `<Redirect to={from} />`).
    const persistIntent = () => {
      try {
        sessionStorage.setItem(
          "pendingBrandQuote",
          JSON.stringify({ brandId, product, ts: Date.now() }),
        );
      } catch (_) {
        /* sessionStorage may be disabled */
      }
    };

    if (!authToken) {
      persistIntent();
      toast.info("Sign in to request a quote — we'll bring you right back.");
      history.push({
        pathname: "/login",
        state: { from: { pathname: `/brand/shop/${brandId}`, search: withOutletQuery("", outletId) } },
      });
      return;
    }

    // The brand-shop modal handles channel selection (in-platform / WhatsApp /
    // email / phone) and dispatches vendor + admin emails on submit. Always
    // route there — single, consistent flow.
    persistIntent();
    history.push({
      pathname: `/brand/shop/${brandId}`,
      search: withOutletQuery("", outletId),
      state: { openQuoteProduct: product },
    });
  };

  const specDelta = computeDelta(selectedSpecs);
  /* The posture, once, so the price row, the stepper and the button cannot
     disagree with each other. They used to: this page read `priceOnRequest` in
     its price row and a widened flag in its footer eight hundred lines below,
     so a product at price 0 printed "USh 0" above a Get Quote button. */
  const quoteOnly = isQuoteOnly(product);
  /* The buyer's own progress, resolved beside the posture for the same reason:
     the panel under the price, the primary button and the modal guard all read
     this one answer, so they cannot tell the shopper three different things
     about one request. Null when there is no request, which is when the
     ordinary "Request a quote" affordance belongs. The brand's display name
     goes in so the sentences name the vendor the buyer is waiting on. */
  const quoteProgress = quoteOnly
    ? describeQuoteProgress(
        buyerQuoteRequest,
        brand?.tradingName || brand?.name,
      )
    : null;
  const listedPrice = publishedPrice(product);
  const unpriced = !quoteOnly && listedPrice === null;
  const basePrice = listedPrice ?? 0;
  const effectivePrice = basePrice + specDelta;
  // Vendor discount: the sale price is what the cart line and the server
  // charge, so it is the number shown, with the list price struck before it.
  const onOffer = !quoteOnly && hasSalePrice(product);
  const saleShown = onOffer ? product.salePrice + specDelta : null;
  const listShown = onOffer ? listPriceOf(product) + specDelta : undefined;

  const fixedRows = Object.entries(product.specs || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => {
      const legacy = tplFields.find((field) => field.fieldKey === key);
      return { label: legacy?.label || key, value, unit: legacy?.unit };
    });

  /* This page had no title, description or canonical of its own, so after the
     app loaded every product read as "Lusuku, Your Online Marketplace". The
     crawler copy of the page also carries server-built Product data (the
     og-inject edge function); SEOHead stands down from its own when it does. */
  const seoDescription =
    stripHtmlForSeo(product.description || "").slice(0, 160) ||
    `Buy ${product.name}${brandName ? ` from ${brandName}` : ""} on Lusuku.`;

  return (
    <Layout>
      <SEOHead
        title={product.name}
        description={seoDescription}
        imageUrl={product.image}
        canonicalPath={productPathUnder(
          storefrontPathFor("brand", brand) || `/brand/shop/${brandId}`,
          product,
        )}
        type="product"
        product={{
          _id: product._id,
          name: product.name,
          description: stripHtmlForSeo(product.description || ""),
          imageUrl: product.image,
          price: publishedPrice(product),
          vendorName: brandName,
          available:
            product.enabled !== false &&
            !(typeof product.countInStock === "number" && product.countInStock <= 0),
        }}
      />
      {/* Sticky, dismissible, mobile-only. Someone who scanned the QR without
          the app is already on the page they wanted — this just offers the
          app, it does not hijack them to a store listing. */}
      {appBanner}
      <Wrap style={getBrandThemeCssVars(brand)}>
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadLink onClick={() => history.push(shopPath)}>
            ← {brand.tradingName || brand.name}
          </BreadLink>
          <BreadSep>/</BreadSep>
          <BreadCurrent>{product.name}</BreadCurrent>
        </Breadcrumb>

        <PageGrid ref={detailColRef}>
          {/* LEFT — images */}
          <ImageCol>
            <ProductImageGallery
              images={[
                product.image,
                ...(Array.isArray(product.additionalImages)
                  ? product.additionalImages
                  : []),
              ].filter(Boolean)}
              videos={product.videos}
              alt={product.name}
              placeholder={placeholderImage}
              detailRef={detailColRef}
              hoverZoom
              lightbox
              mainHeight="460px"
              lensTint="rgba(201, 162, 39, 0.15)"
              overlay={
                onOffer ? (
                  <DealTag
                    discount={product.discount}
                    size="l"
                    radius={12}
                    zIndex={2}
                  />
                ) : null
              }
            />
          </ImageCol>

          {/* RIGHT — details */}
          <DetailCol>
            <ProductTitle>{product.name}</ProductTitle>
            {product.skuCode && <SkuLine>{product.skuCode}</SkuLine>}

            {/* Price */}
            <PriceBlock>
              {quoteOnly ? (
                <PriceText>{PRICE_ON_REQUEST_LABEL}</PriceText>
              ) : unpriced ? (
                /* Never a confident zero. A blank price is a question; "USh 0"
                   is a wrong answer to it. */
                <PriceText>{PRICE_UNAVAILABLE_LABEL}</PriceText>
              ) : product.priceIsTeaser ? (
                <PriceCut
                  component={PriceText}
                  price={basePrice}
                  listPrice={listShown}
                  salePrice={saleShown}
                  format={fmtUgx}
                  prefix="From "
                  savings={unitSavingsOf(product)}
                  endsAt={product.discount?.endsAt}
                />
              ) : (
                <PriceCut
                  component={PriceText}
                  price={specDelta !== 0 ? effectivePrice : basePrice}
                  listPrice={listShown}
                  salePrice={saleShown}
                  format={fmtUgx}
                  savings={unitSavingsOf(product)}
                  endsAt={product.discount?.endsAt}
                />
              )}
            </PriceBlock>

            {/* WHERE THE REQUEST HAS GOT TO, directly under the price it is the
                answer to. Rendered whenever there is a request at all, live or
                finished: "Quote declined" is as much a thing the buyer came
                back to find out as "Quote received", and the button above the
                fold is not the place to explain either. */}
            {quoteProgress && (
              <QuoteProgressNote $live={quoteProgress.live}>
                <QuoteProgressTitle>{quoteProgress.title}</QuoteProgressTitle>
                <QuoteProgressDetail>{quoteProgress.detail}</QuoteProgressDetail>
                <QuoteProgressLink
                  type="button"
                  onClick={() => history.push("/my_profile/my_quotes")}
                >
                  {quoteProgress.action}
                </QuoteProgressLink>
              </QuoteProgressNote>
            )}

            {/* Stock. Head office's count, so it is left out where this
                branch cannot sell the product: the badge below says why. */}
            {!branchBlocked && (
            <StockRow>
              <StockLabel>Stock:</StockLabel>
              <StockVal $ok={product.countInStock > 0}>
                {product.countInStock > 0
                  ? `${formatCount(product.countInStock)} available`
                  : "Out of stock"}
              </StockVal>
            </StockRow>
            )}

            {/* Sold out or switched off at this branch, and where to get it
                instead (4 Oct 2026). Nothing when it can be ordered here. */}
            {!quoteOnly && (
              <BranchAvailabilityBadge
                product={product}
                branchName={hereName}
                locations={brandLocations}
                vendorName={brandName}
                onGetItFrom={getItFrom}
                accent={primary}
              />
            )}

            {/* Fulfilment chips */}
            {(product.measurements || product.minOrderQty > 1 || product.leadTimeDays > 0) && (
              <ChipRow>
                {/* Selling unit, separate from the customer's size choice. */}
                {product.measurements &&
                  typeof product.measurements !== "object" && (
                    <FulfilChip>
                      Unit: {product.measurements}
                      {product.unit || product.unit_id?.unit
                        ? ` ${product.unit || product.unit_id.unit}`
                        : ""}
                    </FulfilChip>
                  )}
                {product.minOrderQty > 1 && (
                  <FulfilChip
                    $pulse={atMin}
                    $met={quantity >= product.minOrderQty}
                  >
                    Min. order: {formatCount(product.minOrderQty)} units
                  </FulfilChip>
                )}
                {product.leadTimeDays > 0 && (
                  <FulfilChip $green>
                    Lead time: ~{product.leadTimeDays} day
                    {product.leadTimeDays !== 1 ? "s" : ""}
                  </FulfilChip>
                )}
              </ChipRow>
            )}

            {/* Fixed specs table */}
            {fixedRows.length > 0 && (
              <SpecSection>
                <SpecSectionTitle>Specifications</SpecSectionTitle>
                <SpecGrid>
                  {fixedRows.map((r, i) => (
                    <React.Fragment key={i}>
                      <SpecLabel>{r.label}</SpecLabel>
                      <SpecValue>
                        {r.value}
                        {r.unit ? ` ${r.unit}` : ""}
                      </SpecValue>
                    </React.Fragment>
                  ))}
                </SpecGrid>
              </SpecSection>
            )}

            {/* Buyer-configurable variants */}
            <BrandProductOptions options={labeledOptions} selectedSpecs={selectedSpecs}
              onChange={setSelectedSpecs} basePrice={quoteOnly || unpriced ? null : onOffer ? Number(product.salePrice) : basePrice}
              format={fmtUgx} />

            {/* Quantity + CTA. An unpriced SKU gets no stepper either: every
                press would edit a cart line the server refuses. */}
            {!quoteOnly && !unpriced && (
              <QtySection>
                <QtyLabel>{cartQuantity > 0 ? "In your cart" : "Quantity"}</QtyLabel>
                <QtyControls>
                  <QtyBtn
                    type="button"
                    $shake={atMin}
                    $danger={removesLine}
                    aria-label={removesLine ? "Remove from cart" : "Decrease quantity"}
                    title={removesLine ? "Remove from cart" : undefined}
                    onClick={() => handleStep(stepDown)}
                  >
                    {removesLine ? (
                      <DeleteOutlineIcon style={{ fontSize: 22 }} />
                    ) : (
                      "−"
                    )}
                  </QtyBtn>
                  <QtyDisplay aria-live="polite">
                    {formatCount(shownQty)}
                  </QtyDisplay>
                  <QtyBtn
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => handleStep(stepUp)}
                    disabled={stepUp.type === "BLOCKED"}
                  >
                    +
                  </QtyBtn>
                </QtyControls>
              </QtySection>
            )}

            {(() => {
              const ctaAction = brandCtaAction({
                priceHidden: quoteOnly,
                unpriced,
                inCartQty: cartQuantity,
                quoteProgress,
              });
              const label = {
                QUOTE: "Request a quote",
                /* The progress action itself, so the button says what the panel
                   above it says. It falls back only for a lifecycle that has
                   somehow arrived without one. */
                QUOTE_IN_PROGRESS: quoteProgress?.action || "View my request",
                QUOTE_AGAIN: "Ask again",
                UNAVAILABLE: "No price yet, cannot be ordered",
                GO_TO_CART: `Go to cart · ${formatCount(cartQuantity)} in cart · ${fmtUgx(effectivePrice * cartQuantity)}`,
                ADD: branchBlocked
                  ? addButtonLabel(product)
                  : `Add to cart · ${fmtUgx(effectivePrice * quantity)}`,
              }[ctaAction];
              return (
                <CtaButton
                  onClick={
                    {
                      QUOTE: handleRequestQuote,
                      /* A live request has nothing to start, so the button is
                         the way to it rather than the way to a second one. */
                      QUOTE_IN_PROGRESS: () =>
                        history.push("/my_profile/my_quotes"),
                      QUOTE_AGAIN: handleRequestQuote,
                      UNAVAILABLE: undefined,
                      GO_TO_CART: () => history.push("/cart"),
                      ADD: handleAddToCart,
                    }[ctaAction]
                  }
                  $quote={quoteOnly}
                  $inCart={ctaAction === "GO_TO_CART"}
                  /* A quote is always available on a quote-only product, even a
                     switched-off one. An unpriced one has nothing to offer. */
                  disabled={
                    quoteOnly
                      ? false
                      : unpriced
                        ? true
                        : !product.enabled ||
                          (branchBlocked && ctaAction === "ADD")
                  }
                >
                  {label}
                </CtaButton>
              );
            })()}

            {!product.enabled && (
              <UnavailNote>This product is currently unavailable.</UnavailNote>
            )}

            {/* Share action */}
            <ShareRow>
              <span style={{ flex: 1 }} />
              <Tooltip title={linkCopied ? "Copied!" : "Share product"}>
                <button type="button" onClick={handleShare}>
                  <ShareIcon style={{ fontSize: 16 }} /> Share
                </button>
              </Tooltip>
            </ShareRow>

            {/* Trust badges — derived strictly from backend brand data */}
            {(brand.verifiedVendorBadge !== false ||
              brand.fulfilment?.deliveryCoverage ||
              brand.fulfilment?.minimumOrderValueUgx ||
              brand.fulfilment?.leadTimeStandardDays != null) && (
              <TrustStrip>
                {brand.verifiedVendorBadge !== false && (
                  <TrustCell>
                    <OfficialStoreBadge size="small" />
                  </TrustCell>
                )}
                {brand.fulfilment?.deliveryCoverage && (
                  <TrustCell>
                    <LocalShippingOutlinedIcon />
                    <span>
                      {String(brand.fulfilment.deliveryCoverage)
                        .replace(/_/g, " ")
                        .toLowerCase()}
                    </span>
                  </TrustCell>
                )}
                {brand.fulfilment?.leadTimeStandardDays != null && (
                  <TrustCell>
                    <LocalShippingOutlinedIcon />
                    <span>
                      Ships in ~{brand.fulfilment.leadTimeStandardDays} day
                      {brand.fulfilment.leadTimeStandardDays !== 1 ? "s" : ""}
                    </span>
                  </TrustCell>
                )}
                {brand.fulfilment?.minimumOrderValueUgx ? (
                  <TrustCell>
                    <SupportAgentOutlinedIcon />
                    <span>
                      Min. order {fmtUgx(brand.fulfilment.minimumOrderValueUgx)}
                    </span>
                  </TrustCell>
                ) : null}
                {brand.fulfilment?.returnsPolicyText && (
                  <TrustCell>
                    <ReplayOutlinedIcon />
                    <span>Returns supported</span>
                  </TrustCell>
                )}
              </TrustStrip>
            )}
          </DetailCol>
        </PageGrid>

        {/* Information area — tabbed, full width below the fold */}
        <InfoSection>
          <InfoTabBar>
            <Tabs
              value={activeInfoTab}
              onChange={(_, v) => setActiveInfoTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Description" value="description" />
              {fixedRows.length > 0 && (
                <Tab label="Specifications" value="specs" />
              )}
              <Tab label="Shipping & Returns" value="shipping" />
              <Tab
                label={`About ${brand.tradingName || brand.name}`}
                value="brand"
              />
            </Tabs>
          </InfoTabBar>
          <InfoTabBody>
            {activeInfoTab === "description" &&
              (product.description ? (
                <DescBody>
                  <ReactQuill
                    value={product.description}
                    readOnly
                    theme="bubble"
                  />
                </DescBody>
              ) : (
                <MutedNote>No description provided.</MutedNote>
              ))}

            {activeInfoTab === "specs" && fixedRows.length > 0 && (
              <SpecGrid>
                {fixedRows.map((r, i) => (
                  <React.Fragment key={i}>
                    <SpecLabel>{r.label}</SpecLabel>
                    <SpecValue>
                      {r.value}
                      {r.unit ? ` ${r.unit}` : ""}
                    </SpecValue>
                  </React.Fragment>
                ))}
              </SpecGrid>
            )}

            {activeInfoTab === "shipping" &&
              (() => {
                const f = brand.fulfilment || {};
                const productLead =
                  product.leadTimeDays > 0 ? product.leadTimeDays : null;
                const brandLead =
                  f.leadTimeStandardDays != null
                    ? f.leadTimeStandardDays
                    : null;
                const lead = productLead ?? brandLead;
                const hasAny =
                  lead != null ||
                  f.deliveryCoverage ||
                  f.minimumOrderValueUgx ||
                  f.returnsPolicyText ||
                  f.paymentTermsOffered?.length;
                if (!hasAny) {
                  return (
                    <MutedNote>
                      Shipping & returns details are not configured for this
                      brand yet.
                    </MutedNote>
                  );
                }
                return (
                  <ShippingList>
                    {f.deliveryCoverage && (
                      <li>
                        <LocalShippingOutlinedIcon />{" "}
                        <span>
                          Delivery coverage:{" "}
                          {String(f.deliveryCoverage)
                            .replace(/_/g, " ")
                            .toUpperCase()}
                          .
                        </span>
                      </li>
                    )}
                    {lead != null && (
                      <li>
                        <LocalShippingOutlinedIcon />{" "}
                        <span>
                          Typical lead time ~{lead} day{lead !== 1 ? "s" : ""}
                          {productLead && brandLead && productLead !== brandLead
                            ? " for this product"
                            : ""}
                          .
                        </span>
                      </li>
                    )}
                    {f.minimumOrderValueUgx ? (
                      <li>
                        <SupportAgentOutlinedIcon />{" "}
                        <span>
                          Minimum order value: {fmtUgx(f.minimumOrderValueUgx)}.
                        </span>
                      </li>
                    ) : null}
                    {f.paymentTermsOffered?.length ? (
                      <li>
                        <LockOutlinedIcon />{" "}
                        <span>
                          Payment terms: {f.paymentTermsOffered.join(", ")}.
                        </span>
                      </li>
                    ) : null}
                    {f.returnsPolicyText && (
                      <li>
                        <ReplayOutlinedIcon />{" "}
                        <span>{f.returnsPolicyText}</span>
                      </li>
                    )}
                  </ShippingList>
                );
              })()}

            {activeInfoTab === "brand" && (
              <BrandCardBlock>
                <BrandHead>
                  {brand.logo && (
                    <BrandLogo src={brand.logo} alt={brand.name} />
                  )}
                  <div>
                    <BrandName>{brand.tradingName || brand.name}</BrandName>
                    {brand.tagline && (
                      <BrandTagline>{brand.tagline}</BrandTagline>
                    )}
                  </div>
                </BrandHead>
                {brand.description && (
                  <BrandDesc
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(brand.description),
                    }}
                  />
                )}
                <VisitBrandBtn
                  onClick={() => history.push(shopPath)}
                >
                  Visit {brand.tradingName || brand.name} store →
                </VisitBrandBtn>
              </BrandCardBlock>
            )}
          </InfoTabBody>
        </InfoSection>

        {/* Per-product reviews — anchored to this brand product. */}
        {product?._id && (
          <Reviews
            subjectType="product_brand"
            subjectId={product._id}
            title="Reviews for this product"
          />
        )}

        {/* Floating cart → unified Cart Hub (single cart surface) */}
        <FloatingCart
          $primary={secondary}
          $text={onSecondary}
          onClick={() => history.push("/cart")}
        >
          <Badge
            badgeContent={cartCount > 0 ? cartCount : null}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: primary,
                color: onPrimary,
              },
            }}
          >
            <ShoppingCartIcon />
          </Badge>
        </FloatingCart>

        <Modal
          open={showCart}
          onClose={() => setShowCart(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Fade in={showCart}>
            <div style={{ outline: "none" }}>
              <BrandCart
                onClose={() => setShowCart(false)}
                brandId={brandId}
                brand={brand}
              />
            </div>
          </Fade>
        </Modal>
        <OutletSwitchDialog
          open={Boolean(pendingOutletAdd)}
          fromLabel={
            cartOutlet
              ? findOutlet(brand, cartOutlet)?.name || brandName
              : locationName({ isOutlet: false }, brandName)
          }
          toLabel={hereName}
          onCancel={() => setPendingOutletAdd(null)}
          onConfirm={confirmOutletAdd}
        />
        <OutletSwitchDialog
          open={Boolean(pendingGetItFrom)}
          fromLabel={
            cartOutlet
              ? findOutlet(brand, cartOutlet)?.name || brandName
              : locationName({ isOutlet: false }, brandName)
          }
          toLabel={locationName(pendingGetItFrom, brandName)}
          onCancel={() => setPendingGetItFrom(null)}
          onConfirm={() => {
            const picked = pendingGetItFrom;
            setPendingGetItFrom(null);
            if (picked) openWithCart(picked);
          }}
        />
        <BranchCartReviewDialog {...branchCheck.dialogProps} />
      </Wrap>
    </Layout>
  );
};

export default BrandProductPage;

// ── Styled components ───────────────────────────────────────────────────────

const Wrap = styled.div`
  max-width: 1400px;
  margin: 2rem auto;
  padding: 0 32px;
  min-height: 60vh;
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

const BreadLink = styled.button`
  background: none;
  border: none;
  color: var(--brand-primary, #324d54);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  &:hover {
    text-decoration: underline;
  }
`;

const BreadSep = styled.span`
  color: #94a3b8;
`;

const BreadCurrent = styled.span`
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

/* minmax(0, ...) and min-width: 0 on both columns: a bare "1fr" is
   minmax(auto, 1fr), whose floor is the column's min-content, so a wide child
   (the thumbnail strip, a long SKU) widened the column past the phone. */
const PageGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 45fr) minmax(0, 55fr);
  gap: 2.5rem;
  align-items: start;
  position: relative;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.5rem;
  }
`;

const ImageCol = styled.div`
  position: sticky;
  top: 80px;
  min-width: 0;
`;


const DetailCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const ProductTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
`;

const SkuLine = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  font-family: monospace;
  letter-spacing: 0.04em;
`;

const PriceBlock = styled.div``;

const PriceText = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: var(--brand-primary, #324d54);
`;

/* The same notice shape the page already uses (UnavailNote, StockRow): a
   tinted, bordered, rounded block in the detail column. A live request is
   drawn in the brand's own colour because it is an open conversation with that
   vendor; a finished one goes grey, so "Quote declined" does not shout in the
   same voice as "Quote received". */
const QuoteProgressNote = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  background: ${(p) => (p.$live ? "#f0f7ff" : "#f8fafc")};
  border: 1px solid ${(p) => (p.$live ? "#bfdbfe" : "#e2e8f0")};
  border-left: 3px solid
    ${(p) => (p.$live ? "var(--brand-primary, #324d54)" : "#cbd5e1")};
  border-radius: 8px;
  padding: 10px 14px;
`;

const QuoteProgressTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 800;
  color: #1a1a2e;
`;

const QuoteProgressDetail = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
`;

const QuoteProgressLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--brand-primary, #324d54);
  text-decoration: underline;
  cursor: pointer;
`;

const StockRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;
`;

const StockLabel = styled.span`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
`;

const StockVal = styled.span`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(p) => (p.$ok ? "var(--brand-primary, #324d54)" : "#dc2626")};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const shakeAnim = keyframes`
  0%{transform:translateX(0)}15%{transform:translateX(-5px)}30%{transform:translateX(5px)}
  45%{transform:translateX(-4px)}60%{transform:translateX(4px)}75%{transform:translateX(-2px)}
  90%{transform:translateX(2px)}100%{transform:translateX(0)}
`;

const pulseAnim = keyframes`
  0%{box-shadow:0 0 0 0 rgba(10,87,139,.55)}50%{box-shadow:0 0 0 6px rgba(10,87,139,0)}100%{box-shadow:0 0 0 0 rgba(10,87,139,0)}
`;

const FulfilChip = styled.span`
  background: ${(p) =>
    p.$green
      ? "#f0fdf4"
      : p.$pulse
        ? "#fee2e2"
        : p.$met
          ? "#dcfce7"
          : "#f0f7ff"};
  border: 1px solid
    ${(p) =>
      p.$green
        ? "#bbf7d0"
        : p.$pulse
          ? "#fca5a5"
          : p.$met
            ? "#86efac"
            : "#bfdbfe"};
  color: ${(p) =>
    p.$green
      ? "#166534"
      : p.$pulse
        ? "#b91c1c"
        : p.$met
          ? "#166534"
          : "#0a578b"};
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  animation: ${(p) => (p.$pulse ? pulseAnim : "none")} 0.55s ease;
`;

const SpecSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 18px;
`;

const SpecSectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 12px;
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
`;

const SpecLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SpecValue = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a2e;
`;

const QtySection = styled.div``;

const QtyLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const QtyBtn = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid
    ${(p) =>
      p.$shake
        ? "#dc2626"
        : p.$danger
          ? "#b42318"
          : "var(--brand-primary, #324d54)"};
  background: ${(p) => (p.$shake ? "#fef2f2" : "white")};
  color: ${(p) =>
    p.$shake
      ? "#dc2626"
      : p.$danger
        ? "#b42318"
        : "var(--brand-primary, #324d54)"};
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.15s,
    background 0.15s,
    color 0.15s;
  animation: ${(p) => (p.$shake ? shakeAnim : "none")} 0.55s ease;
  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$danger ? "#b42318" : "var(--brand-primary, #324d54)"};
    color: ${(p) => (p.$danger ? "#fff" : "var(--brand-on-primary, #fff)")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QtyDisplay = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a2e;
  min-width: 52px;
  text-align: center;
`;

const CtaButton = styled.button`
  width: 100%;
  padding: 1.1rem 1.5rem;
  background: ${(p) =>
    p.$inCart ? "#166534" : "var(--brand-secondary, #c9a227)"};
  color: ${(p) =>
    p.$inCart ? "#fff" : "var(--brand-on-secondary, #0f172a)"};
  border: none;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 800;
  cursor: pointer;
  transition:
    opacity 0.2s,
    transform 0.15s,
    background 0.2s;
  margin-top: 0.5rem;
  &:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const UnavailNote = styled.div`
  text-align: center;
  font-size: 0.85rem;
  color: #f97316;
  font-weight: 600;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 8px 14px;
`;

const DescBody = styled.div`
  font-size: 1rem;
  color: #334155;
  line-height: 1.75;
  .ql-editor {
    padding: 0;
  }
`;

const InfoSection = styled.section`
  margin-top: 3rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
`;

const InfoTabBar = styled.div`
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  .MuiTab-root {
    text-transform: none;
    font-weight: 600;
    font-size: 0.92rem;
    min-height: 48px;
    color: #475569;
  }
  .Mui-selected {
    color: var(--brand-primary, #324d54) !important;
  }
  .MuiTabs-indicator {
    background-color: var(--brand-primary, #324d54) !important;
    height: 3px;
    border-radius: 3px 3px 0 0;
  }
`;

const InfoTabBody = styled.div`
  padding: 24px 26px;
  min-height: 220px;
  font-size: 0.95rem;
  color: #1f2937;
  line-height: 1.7;
  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const MutedNote = styled.p`
  font-style: italic;
  color: #64748b;
  margin: 0;
`;

const ShippingList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: #334155;
    font-size: 0.92rem;
    line-height: 1.55;
    svg {
      color: var(--brand-primary, #324d54);
      width: 22px;
      height: 22px;
      flex-shrink: 0;
      margin-top: 2px;
    }
  }
`;

const BrandCardBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const BrandHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const BrandLogo = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  object-fit: cover;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
`;

const BrandName = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: #1a1a2e;
`;

const BrandTagline = styled.div`
  font-size: 0.85rem;
  color: #64748b;
`;

const BrandDesc = styled.div`
  font-size: 0.92rem;
  color: #334155;
  margin: 0;
  p {
    margin: 0 0 8px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  line-height: 1.65;
`;

const VisitBrandBtn = styled.button`
  align-self: flex-start;
  padding: 10px 18px;
  border-radius: 999px;
  border: 1.5px solid var(--brand-primary, #324d54);
  background: transparent;
  color: var(--brand-primary, #324d54);
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  &:hover {
    background: var(--brand-primary, #324d54);
    color: var(--brand-on-primary, #fff);
  }
`;

const TrustStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding-top: 8px;
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TrustCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 6px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  text-align: center;
  font-size: 0.72rem;
  color: #475569;
  font-weight: 600;
  line-height: 1.3;
  svg {
    color: var(--brand-primary, #324d54);
    width: 22px;
    height: 22px;
  }
`;

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 0.82rem;
  color: #64748b;
  button {
    background: none;
    border: none;
    color: var(--brand-primary, #324d54);
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    &:hover {
      background: #f1f5f9;
    }
  }
`;

const FloatingCart = styled(IconButton)`
  position: fixed !important;
  bottom: 30px;
  right: 30px;
  background: ${(p) => p.$primary || "#324d54"} !important;
  color: ${(p) => p.$text || "#fff"} !important;
  width: 60px !important;
  height: 60px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25) !important;
  z-index: 999;
  &:hover {
    opacity: 0.88;
  }
`;
